// MCHD Room Scheduler - Auto-Save Hook
// Custom hook for automatic form state persistence using localStorage
// Created: 08-10-2025 18:40:25

import { useEffect, useCallback, useRef } from 'react';
import { notifications } from '@mantine/notifications';

// =============================================================================
// TYPES
// =============================================================================

export interface AutoSaveOptions {
  key: string;              // Unique storage key for this form
  debounceMs?: number;      // Debounce delay (default: 500ms)
  ttl?: number;             // Time-to-live in milliseconds (default: 24 hours)
  onRestore?: () => void;   // Callback when data is restored
  onSave?: () => void;      // Callback when data is saved
  onClear?: () => void;     // Callback when data is cleared
  enabled?: boolean;        // Enable/disable auto-save (default: true)
}

interface StorageEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

// =============================================================================
// LOCALSTORAGE UTILITIES
// =============================================================================

/**
 * Check if localStorage is available
 * @returns True if localStorage is available
 */
function isStorageAvailable(): boolean {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Safe write to localStorage with quota handling
 * @param key - Storage key
 * @param value - Value to store
 * @returns True if successful
 */
function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, clearing old entries');
      // Attempt to clear old entries
      clearExpiredEntries();
      // Retry once
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (retryError) {
        console.error('Failed to save after clearing:', retryError);
        return false;
      }
    }
    console.error('Error saving to localStorage:', e);
    return false;
  }
}

/**
 * Clear all expired entries from localStorage
 */
function clearExpiredEntries(): void {
  const keys = Object.keys(localStorage);
  const now = Date.now();

  keys.forEach((key) => {
    if (key.startsWith('mchd-autosave-')) {
      try {
        const item = localStorage.getItem(key);
        if (item) {
          const parsed = JSON.parse(item);
          if (parsed.ttl && parsed.ttl < now) {
            localStorage.removeItem(key);
          }
        }
      } catch (e) {
        // Invalid JSON, remove it
        localStorage.removeItem(key);
      }
    }
  });
}

// =============================================================================
// AUTO-SAVE HOOK
// =============================================================================

/**
 * Custom hook for automatic form state persistence
 * @param formData - Current form data
 * @param options - Auto-save options
 * @returns Auto-save utilities
 */
export function useAutoSave<T extends Record<string, any>>(
  formData: T,
  options: AutoSaveOptions
) {
  const {
    key,
    debounceMs = 500,
    ttl = 24 * 60 * 60 * 1000, // 24 hours default
    onRestore,
    onSave,
    onClear,
    enabled = true,
  } = options;

  const storageKey = `mchd-autosave-${key}`;
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isRestoredRef = useRef(false);

  /**
   * Save form data to localStorage
   */
  const save = useCallback(() => {
    if (!enabled || !isStorageAvailable()) {
      return;
    }

    const entry: StorageEntry<T> = {
      data: formData,
      timestamp: Date.now(),
      ttl: Date.now() + ttl,
    };

    const success = safeSetItem(storageKey, JSON.stringify(entry));
    
    if (success) {
      onSave?.();
    }
  }, [formData, storageKey, ttl, enabled, onSave]);

  /**
   * Restore form data from localStorage
   * @returns Restored data or null
   */
  const restore = useCallback((): T | null => {
    if (!enabled || !isStorageAvailable()) {
      return null;
    }

    try {
      const saved = localStorage.getItem(storageKey);
      
      if (!saved) {
        return null;
      }

      const entry: StorageEntry<T> = JSON.parse(saved);

      // Check TTL
      if (entry.ttl < Date.now()) {
        localStorage.removeItem(storageKey);
        return null;
      }

      onRestore?.();
      
      return entry.data;
    } catch (error) {
      console.error('Error restoring from localStorage:', error);
      // Remove corrupted entry
      localStorage.removeItem(storageKey);
      return null;
    }
  }, [storageKey, enabled, onRestore]);

  /**
   * Clear saved form data
   */
  const clear = useCallback(() => {
    if (!isStorageAvailable()) {
      return;
    }

    try {
      localStorage.removeItem(storageKey);
      onClear?.();
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }, [storageKey, onClear]);

  /**
   * Check if saved data exists
   * @returns True if saved data exists and is valid
   */
  const hasSavedData = useCallback((): boolean => {
    if (!enabled || !isStorageAvailable()) {
      return false;
    }

    try {
      const saved = localStorage.getItem(storageKey);
      
      if (!saved) {
        return false;
      }

      const entry: StorageEntry<T> = JSON.parse(saved);
      
      // Check if not expired
      return entry.ttl >= Date.now();
    } catch (error) {
      return false;
    }
  }, [storageKey, enabled]);

  /**
   * Auto-restore on mount
   */
  useEffect(() => {
    if (!enabled || isRestoredRef.current) {
      return;
    }

    const restoredData = restore();
    
    if (restoredData) {
      notifications.show({
        title: 'Draft Restored',
        message: 'Your previous work was automatically saved and has been restored',
        color: 'blue',
        autoClose: 5000,
      });
    }

    isRestoredRef.current = true;
  }, [enabled, restore]);

  /**
   * Auto-save on form data change (debounced)
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      save();
    }, debounceMs);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [formData, debounceMs, enabled, save]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    save,
    restore,
    clear,
    hasSavedData,
  };
}

/**
 * Hook variant that returns restored data immediately
 * @param options - Auto-save options
 * @returns Restored data and utilities
 */
export function useAutoSaveWithRestore<T extends Record<string, any>>(
  options: AutoSaveOptions
): {
  restoredData: T | null;
  save: (data: T) => void;
  clear: () => void;
  hasSavedData: () => boolean;
} {
  const storageKey = `mchd-autosave-${options.key}`;
  const ttl = options.ttl || 24 * 60 * 60 * 1000;

  const save = useCallback((data: T) => {
    if (!isStorageAvailable()) {
      return;
    }

    const entry: StorageEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: Date.now() + ttl,
    };

    safeSetItem(storageKey, JSON.stringify(entry));
    options.onSave?.();
  }, [storageKey, ttl, options]);

  const restore = useCallback((): T | null => {
    if (!isStorageAvailable()) {
      return null;
    }

    try {
      const saved = localStorage.getItem(storageKey);
      
      if (!saved) {
        return null;
      }

      const entry: StorageEntry<T> = JSON.parse(saved);

      if (entry.ttl < Date.now()) {
        localStorage.removeItem(storageKey);
        return null;
      }

      options.onRestore?.();
      
      return entry.data;
    } catch (error) {
      console.error('Error restoring:', error);
      localStorage.removeItem(storageKey);
      return null;
    }
  }, [storageKey, options]);

  const clear = useCallback(() => {
    if (!isStorageAvailable()) {
      return;
    }

    localStorage.removeItem(storageKey);
    options.onClear?.();
  }, [storageKey, options]);

  const hasSavedData = useCallback((): boolean => {
    if (!isStorageAvailable()) {
      return false;
    }

    try {
      const saved = localStorage.getItem(storageKey);
      
      if (!saved) {
        return false;
      }

      const entry: StorageEntry<T> = JSON.parse(saved);
      return entry.ttl >= Date.now();
    } catch (error) {
      return false;
    }
  }, [storageKey]);

  // Restore on mount
  const restoredData = restore();

  return {
    restoredData,
    save,
    clear,
    hasSavedData,
  };
}

